import { Suspense, lazy, useTransition } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useAuth } from './lib/auth/AuthProvider';
import { Navigation } from './components/v0/Navigation';
import { TaskList } from './pages/v0/TaskList';
import { ProjectList } from './pages/v0/ProjectList';
import { ProjectDetail } from './pages/v0/ProjectDetail';
import { SharedProjectDetail } from './pages/v0/SharedProjectDetail';
import { Chat } from './pages/v0/Chat';
import { Me } from './pages/v0/Me';
import { Calendar } from './pages/v0/Calendar'; // Assuming Calendar is still needed directly? Or CalendarPageWrapper? Using CalendarPageWrapper based on App.tsx usage
import { TagsPage } from './pages/v0/TagsPage';
import { PWAInstall } from './components/v0/PWAInstall';
import Subscription from './pages/v0/subscription/Subscription';
import SubscriptionSuccess from './pages/v0/subscription/Success';
import SubscriptionCancel from './pages/v0/subscription/Cancel';
import { CalendarPageWrapper } from './pages/v0/CalendarPageWrapper';
import NotificationTest from './pages/v0/NotificationTest';
import { useTaskReminders } from './services/TaskNotificationHelper';

// 导入 v0 专用样式
import './styles/v0.css';

// Moved PrivateRoute here as it guards V0 routes specifically
const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
    const { user, loading } = useAuth();

    // Add Task Reminders Hook specific to V0 authenticated routes
    useTaskReminders();

    console.log('[PrivateRouteV0] Evaluating route. Auth state:', { loading, userExists: !!user });

    if (loading) {
        console.log('[PrivateRouteV0] Auth loading, rendering loading indicator.');
        return <div className="w-full h-screen flex items-center justify-center">Loading V0...</div>;
    }

    if (!user) {
        console.log('[PrivateRouteV0] No user found, redirecting to /login.');
        return <Navigate to="/login" replace />;
    }

    console.log('[PrivateRouteV0] User authenticated, rendering protected content.');
    return (
        <Suspense fallback={<div className="w-full h-screen flex items-center justify-center">Loading content...</div>}>
            <div className="v0-app w-full min-h-screen">
                {children}
                {/* Navigation and PWAInstall are part of the V0 layout */}
                <Navigation />
                <PWAInstall />
            </div>
        </Suspense>
    );
};

// const TasksPage = lazy(() => import('./pages/v0/TasksPage'));
// const ProjectPage = lazy(() => import('./pages/v0/ProjectPage'));
// const SubscriptionPage = lazy(() => import('./pages/v0/subscription/SubscriptionPage'));

export function AppV0() {
    return (
        <Routes>
            {/* Keep /login and /auth/callback at the top level in App.tsx */}
            {/* V0 Routes */}
            <Route
                path="/tasks" // Becomes /v0/tasks
                element={
                    <PrivateRoute>
                        <TaskList />
                    </PrivateRoute>
                }
            />
            <Route
                path="/tasks/:id" // Becomes /v0/tasks/:id
                element={
                    <PrivateRoute>
                        <TaskList />
                    </PrivateRoute>
                }
            />
            <Route
                path="/projects" // Becomes /v0/projects
                element={
                    <PrivateRoute>
                        <ProjectList />
                    </PrivateRoute>
                }
            />
            <Route
                path="/projects/:id" // Becomes /v0/projects/:id
                element={
                    <PrivateRoute>
                        <ProjectDetail />
                    </PrivateRoute>
                }
            />
            <Route
                path="/calendar" // Becomes /v0/calendar
                element={
                    <PrivateRoute>
                        <CalendarPageWrapper />
                    </PrivateRoute>
                }
            />
            <Route
                path="/chat" // Becomes /v0/chat
                element={
                    <PrivateRoute>
                        <Chat />
                    </PrivateRoute>
                }
            />
            <Route
                path="/me" // Becomes /v0/me
                element={
                    <PrivateRoute>
                        <Me />
                    </PrivateRoute>
                }
            />
            <Route
                path="/notification-test" // Becomes /v0/notification-test
                element={
                    <PrivateRoute>
                        <NotificationTest />
                    </PrivateRoute>
                }
            />
            <Route
                path="/tags" // Becomes /v0/tags
                element={
                    <PrivateRoute>
                        <TagsPage />
                    </PrivateRoute>
                }
            />
            <Route
                path="/subscription" // Becomes /v0/subscription
                element={
                    <PrivateRoute>
                        <Subscription />
                    </PrivateRoute>
                }
            />
            <Route
                path="/subscription/success" // Becomes /v0/subscription/success
                element={
                    <PrivateRoute>
                        <SubscriptionSuccess />
                    </PrivateRoute>
                }
            />
            <Route
                path="/subscription/cancel" // Becomes /v0/subscription/cancel
                element={
                    <PrivateRoute>
                        <SubscriptionCancel />
                    </PrivateRoute>
                }
            />
            {/* 分享项目路由 - 不需要认证 */}
            <Route
                path="/s/project/:shareId"
                element={<SharedProjectDetail />}
            />
            {/* Redirect within V0 from /v0/ to /v0/tasks */}
            <Route
                path="/"
                element={<Navigate to="/v0/tasks" replace />}
            />
            {/* Catch-all for unknown v0 routes, maybe redirect or show a 404 */}
            <Route path="*" element={<Navigate to="/v0/tasks" replace />} />
        </Routes>
    );
}